import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import type { StandardApplicability } from '@/shared/hooks/api/machineryDirectiveCompliance'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import {
  Item,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from '@/components/ui/item'
import { StandardDataStatusBadge } from '@/components/StandardDataStatusBadge'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

export type StandardCardSection = 'applicable' | 'rejected'

export type StandardCardProps = {
  standard: StandardApplicability
  section: StandardCardSection
  onAccept: () => void
  onReject: () => void
  isUpdating?: boolean
  canReject?: boolean
  /** Appended to `/standards-library/standards/:id` for "View standard" (e.g. return context query) */
  standardDetailSearch?: string
}

export function StandardCard({
  standard,
  section,
  onAccept,
  onReject,
  isUpdating = false,
  canReject = true,
  standardDetailSearch = '',
}: StandardCardProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')

  const inEffect = standard.inEffect ?? true
  const hasUserStandardId = Boolean(standard.userStandardId)

  const acceptLabel =
    section === 'rejected' && !hasUserStandardId
      ? t('standardsPage.acceptAndAddToStandardsLibrary')
      : t('standardsPage.accept')

  const hasData =
    standard.hasSignificantHazards === true ||
    standard.hasInformationForUse === true ||
    standard.hasRiskReductionMeasures === true

  const hasPdf = standard.hasFile === true

  const effectBadgeVariant = inEffect ? 'outline' : 'secondary'
  const effectBadgeText = inEffect
    ? t('standardsPage.inEffect')
    : t('standardsPage.notInEffect')

  const hasExplanation = standard.explanation !== null
  const isApplicable = section === 'applicable'
  const isRejected = section === 'rejected'
  const typeLabel = `${t('standardsPage.typeLabel')} ${standard.type}`
  const rejectDisabled = isUpdating || !canReject
  const rejectLabel = t('standardsPage.reject')

  let rejectControl = (
    <Button
      size='sm'
      variant='outline'
      onClick={onReject}
      disabled={rejectDisabled}
    >
      {rejectLabel}
    </Button>
  )

  if (!canReject) {
    rejectControl = (
      <Tooltip>
        <TooltipTrigger asChild>
          <span className='inline-flex'>{rejectControl}</span>
        </TooltipTrigger>
        <TooltipContent>{t('standardsPage.cannotRejectLastTypeA')}</TooltipContent>
      </Tooltip>
    )
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle className='flex items-center justify-between gap-2'>
          <div className='flex items-center gap-2'>
            <span>{standard.reference}</span>
            <span className='text-xs font-normal text-muted-foreground'>
              {typeLabel}
            </span>
          </div>
          <div className='flex items-center gap-2'>
            <StandardDataStatusBadge
              hasData={hasData}
              hasPdf={hasPdf}
              requireLinkedUserStandard
              userStandardId={standard.userStandardId}
            />
            <Badge variant={effectBadgeVariant}>{effectBadgeText}</Badge>
          </div>
        </CardTitle>
        <CardDescription>
          <p>{standard.title}</p>
        </CardDescription>
      </CardHeader>
      <CardContent className='space-y-2'>
        {hasExplanation && (
          <Item variant='outline'>
            <ItemContent>
              <ItemTitle>{t('standardsPage.reasonLabel')}</ItemTitle>
              <ItemDescription>{standard.explanation}</ItemDescription>
            </ItemContent>
          </Item>
        )}
      </CardContent>
      <CardFooter className='flex flex-wrap items-center justify-end gap-2'>
        {isApplicable && (
          <>
            {rejectControl}
            {hasUserStandardId && (
              <Button size='sm' variant='secondary' asChild>
                <Link
                  to={`/standards-library/standards/${standard.userStandardId}${standardDetailSearch}`}
                >
                  {t('standardsPage.viewStandard')}
                </Link>
              </Button>
            )}
          </>
        )}
        {isRejected && (
          <>
            <Button
              size='sm'
              variant='default'
              onClick={onAccept}
              disabled={isUpdating}
            >
              {acceptLabel}
            </Button>
            {hasUserStandardId && (
              <Button size='sm' variant='secondary' asChild>
                <Link
                  to={`/standards-library/standards/${standard.userStandardId}${standardDetailSearch}`}
                >
                  {t('standardsPage.viewStandard')}
                </Link>
              </Button>
            )}
          </>
        )}
      </CardFooter>
    </Card>
  )
}
